// component 4: AddItemForm.jsx (Revised and made more dynamic)
import React, { useState, useMemo, useCallback } from "react";
import { FaPlus, FaSpinner, FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import * as lucideIcons from "lucide-react";

const capitalizeFirstLetter = (string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// --- Define field configurations for each fieldName ---
// This is key to making the form dynamic. Expand this as needed.
const iconOptions = Object.keys(lucideIcons); // Get names for dropdown

const gradientOptions = [
  { value: "from-orange-600 to-red-700", label: "Orange to Red" },
  { value: "from-green-400 to-blue-500", label: "Green to Blue" },
  { value: "from-purple-600 to-pink-500", label: "Purple to Pink" },
  { value: "from-yellow-400 to-red-600", label: "Yellow to Red" },
];

const fieldConfigurations = {
  bannerBottom: [
    { name: "imageUrl", type: "file", label: "Image", required: true },
  ],
  banners: [{ name: "imageUrl", type: "file", label: "Image", required: true }],
  featuredProducts: [
    { name: "image", type: "file", label: "Product Image", required: true },
  ],
  features: [
    { name: "header", type: "text", label: "Section Header", required: true },
    {
      name: "features",
      type: "nestedArray",
      label: "Features List",
      required: true,
      nestedFields: [
        {
          name: "icon",
          type: "select",
          label: "Icon",
          options: iconOptions,
          required: false,
        },

        { name: "title", type: "text", label: "Title", required: false },
        {
          name: "description",
          type: "textarea",
          label: "Description",
          required: true,
        },
      ],
      defaultNestedItem: {
        icon: iconOptions[0] || "",
        title: "",
        description: "",
      },
    },
  ],

  history: [
    { name: "header", type: "text", label: "Header", required: true }, // Image is optional based on your example output showing one with and one without
    { name: "imageUrl", type: "file", label: "History Image", required: true },
    {
      name: "features",
      type: "nestedArray",
      label: "History Points",
      required: true,
      nestedFields: [
        {
          name: "icon",
          type: "select",
          label: "Icon",
          options: iconOptions,
          required: false,
        },
        { name: "title", type: "text", label: "Title", required: false },
        {
          name: "description",
          type: "textarea",
          label: "Description",
          required: true,
        },
      ],

      defaultNestedItem: { icon: "History", title: "", description: "" },
    },
  ],

  logos: [
    { name: "imageUrl", type: "file", label: "Logo Image", required: true }, // Changed 'name' to 'imageUrl'
  ],
  promises: [
    { name: "promise", type: "text", label: "Promise Title", required: true },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      required: true,
    },
    {
      name: "gradient",
      type: "select",
      label: "Tailwind Gradient",
      required: false,
      options: gradientOptions,
    },
  ],

  stores: [
    {
      name: "title",
      type: "text",
      label: "Store Title",
      required: true,
      placeholder: "Enter the store name or branch",
    }, // File input for the picture. 'onSave' will handle upload and set 'pictureUrl'
    {
      name: "pictureUrl",
      type: "file",
      label: "Store Picture",
      required: true,
    }, // Keep name 'picture' for file input
    {
      name: "location",
      type: "text",
      label: "Location (e.g., Address or Google Maps URL)",
      required: false,
      placeholder: "Enter address or paste Google Maps link",
    }, // Configuration for features remains the same for input UI
    {
      name: "features", // This is the array field
      type: "nestedArray",
      label: "Store Details",
      required: false,
      addButtonText: "Add Another Detail", // Input structure: Array of objects with a 'detail' key
      nestedFields: [
        {
          name: "detail",
          type: "text",
          label: "Detail",
          required: true,
          placeholder: "e.g., Phone: 555-1234, Hours: 9am-5pm",
        },
      ], // Default item for the input form

      defaultNestedItem: { detail: "" },
    },
  ],
  yearProduct: [
    { name: "title", type: "text", label: "Product Title", required: true },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      required: true,
    },
    { name: "imageUrl", type: "file", label: "Product Image", required: true },
  ],
  default: [{ name: "name", type: "text", label: "Name", required: true }],
};

const AddItemForm = ({ fieldName, onSave, onCancel, isSaving }) => {
  const fields = useMemo(
    () => fieldConfigurations[fieldName] || fieldConfigurations.default,
    [fieldName]
  );

  // Initialize form data, handling nested arrays
  const initialFormData = useMemo(
    () =>
      fields.reduce((acc, field) => {
        if (field.type === "nestedArray") {
          // Initialize with one default item if configured, otherwise empty array
          acc[field.name] = field.defaultNestedItem
            ? [{ ...field.defaultNestedItem }]
            : [];
        } else {
          acc[field.name] = ""; // Initialize other fields as empty strings
        }
        return acc;
      }, {}),
    [fields] // Re-initialize if fields change (fieldName changes)
  );

  const [formData, setFormData] = useState(initialFormData);
  const [imageFile, setImageFile] = useState(null);

  // Reset form when fieldName changes (e.g., closing and reopening modal for different type)
  // Important if the same modal instance is reused
  React.useEffect(() => {
    setFormData(initialFormData);
    setImageFile(null); // Reset image file too
  }, [fieldName, initialFormData]); // Depend on fieldName and the derived initialFormData

  // Handles simple input changes AND changes within nested arrays
  const handleInputChange = useCallback(
    (e, nestedIndex = null, nestedFieldName = null, parentFieldName = null) => {
      if (nestedIndex !== null && parentFieldName) {
        // --- Handling change within a nested array item ---
        const { value } = e.target;
        setFormData((prev) => {
          const updatedNestedArray = [...prev[parentFieldName]]; // Copy the nested array
          if (updatedNestedArray[nestedIndex]) {
            // Check if item exists
            updatedNestedArray[nestedIndex] = {
              // Copy the nested object and update the specific field
              ...updatedNestedArray[nestedIndex],
              [nestedFieldName]: value,
            };
          }
          return {
            ...prev,
            [parentFieldName]: updatedNestedArray, // Set the updated nested array back into state
          };
        });
      } else {
        // --- Handling change for a top-level field ---
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    },
    []
  ); // No dependencies needed as it operates on current state

  // Handles file input change
  const handleFileChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    } else {
      setImageFile(null);
    }
  }, []);

  // Adds a new item to a nested array field
  const handleAddNestedItem = useCallback(
    (parentFieldName) => {
      const parentFieldConfig = fields.find((f) => f.name === parentFieldName);
      if (!parentFieldConfig || !parentFieldConfig.defaultNestedItem) return; // Safety check

      setFormData((prev) => ({
        ...prev,
        [parentFieldName]: [
          ...prev[parentFieldName],
          { ...parentFieldConfig.defaultNestedItem }, // Add a new default item
        ],
      }));
    },
    [fields]
  ); // Depends on the fields config

  // Removes an item from a nested array field by index
  const handleRemoveNestedItem = useCallback(
    (parentFieldName, indexToRemove) => {
      setFormData((prev) => ({
        ...prev,
        [parentFieldName]: prev[parentFieldName].filter(
          (_, index) => index !== indexToRemove
        ), // Filter out the item
      }));
    },
    []
  );

  // Form submission logic
  const handleSubmit = async (e) => {
    e.preventDefault();
    let processedFormData = { ...formData }; // Start with current form state
    let fileInput = null;
    let hasValidationError = false;

    // Validation loop
    for (const field of fields) {
      if (hasValidationError) break; // Stop validation if error already found

      // 1. Handle top-level required fields
      if (field.required && field.type !== "nestedArray") {
        if (field.type === "file") {
          // Assuming only one top-level file input per form for simplicity based on configs
          if (!imageFile) {
            toast.error(`Please select a file for ${field.label}.`);
            hasValidationError = true;
          } else {
            fileInput = imageFile;
            // We don't add file to processedFormData directly, it's handled separately by onSave
          }
        } else if (!formData[field.name]?.trim()) {
          toast.error(`Please fill in the ${field.label}.`);
          hasValidationError = true;
        }
      }

      // 2. Handle JSON textareas (removed as the structure is now nestedArray for stores)
      // If you had other JSON fields, you would keep this logic, but modify it
      // or transition them to nestedArray type.

      // 3. Handle nested array validation
      if (field.type === "nestedArray" && !hasValidationError) {
        const nestedArray = formData[field.name];
        if (field.required && (!nestedArray || nestedArray.length === 0)) {
          toast.error(`Please add at least one item to ${field.label}.`);
          hasValidationError = true;
        } else if (nestedArray) {
          // Validate required fields *within* each nested item
          for (let i = 0; i < nestedArray.length; i++) {
            const nestedItem = nestedArray[i];
            for (const nestedField of field.nestedFields) {
              if (
                nestedField.required &&
                !nestedItem[nestedField.name]?.trim()
              ) {
                toast.error(
                  `Please fill in '${nestedField.label}' for item ${i + 1} in ${
                    field.label
                  }.`
                );
                hasValidationError = true;
                break; // Stop checking this nested item
              }
            }
            if (hasValidationError) break; // Stop checking other nested items
          }
        }
        // The nested array data is already in the correct format (array of objects) in `processedFormData`
        // *unless* it's the 'stores' nested array, which we'll handle below.
      }
    } // End validation loop

    if (hasValidationError) {
      return; // Stop submission if validation failed
    }

    // --- Apply specific transformation for 'stores' nested array ---
    // ONLY if fieldName is 'stores' AND the field is the 'features' nestedArray for stores
    if (fieldName === "stores") {
      const storesFeaturesField = fields.find(
        (f) => f.name === "features" && f.type === "nestedArray"
      );
      if (storesFeaturesField) {
        const nestedArray = formData[storesFeaturesField.name]; // Get the array of objects from state
        const primaryNestedFieldName =
          storesFeaturesField.nestedFields[0]?.name; // Assumes 'detail' for stores

        if (nestedArray && primaryNestedFieldName) {
          // Map array of objects to array of strings, filtering out empty strings/nulls
          processedFormData[storesFeaturesField.name] = nestedArray
            .map(
              (item) => item[primaryNestedFieldName]?.toString().trim() || ""
            ) // Extract value, ensure string, trim
            .filter((value) => value !== ""); // Filter out empty strings
        } else {
          // If no data or no primary field, ensure it's an empty array
          processedFormData[storesFeaturesField.name] = [];
        }
      }
    }

    // Remove file field names from the data object as the file is passed separately
    // This should generally be done *after* any data processing but *before* calling onSave
    fields.forEach((field) => {
      if (field.type === "file") {
        // Assuming the field name for the file is 'imageUrl' or 'pictureUrl' based on config
        // We don't want the File object itself in the JSON data sent to the backend handler
        delete processedFormData[field.name];
      }
    });

    // Call the onSave callback provided by the parent (AllStatic)
    await onSave(processedFormData, fileInput);
  };

  // --- RENDER FORM ---
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-2">
        Add New {capitalizeFirstLetter(fieldName || "Item")}
      </h3>
      {fields.map((field) => (
        <div key={field.name}>
          {/* --- Render Standard Fields (Text, Textarea, File, Select) --- */}
          {field.type !== "nestedArray" && (
            <>
              <label
                htmlFor={field.name}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {field.label || capitalizeFirstLetter(field.name)}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type === "file" ? (
                <input
                  type="file"
                  id={field.name}
                  name={field.name}
                  onChange={handleFileChange}
                  accept="image/*"
                  required={field.required}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              ) : field.type === "textarea" ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  rows={field.isJson ? 5 : 3}
                  required={field.required}
                  placeholder={field.placeholder || ""}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : field.type === "select" ? ( // <<< Updated select rendering
                <select
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  required={field.required}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                >
                  {/* Add a default prompt option */}
                  <option
                    value=""
                    disabled={field.required} // Disable if required, allow selection if optional
                  >
                    {`Select ${
                      field.label || capitalizeFirstLetter(field.name)
                    }...`}
                  </option>
                  {(field.options || []).map((option, index) => {
                    // Determine value and label based on option structure (string or object)
                    const value =
                      typeof option === "string" ? option : option.value;
                    const label =
                      typeof option === "string" ? option : option.label;
                    return (
                      <option key={value || index} value={value}>
                        {" "}
                        {/* Use index as fallback key */}
                        {label}
                      </option>
                    );
                  })}
                </select>
              ) : (
                // Default to text input
                <input
                  type={field.type || "text"}
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  required={field.required}
                  placeholder={field.placeholder || ""}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              )}
              {field.isJson && (
                <p className="text-xs text-gray-500 mt-1">
                  Enter a valid JSON array structure.
                </p>
              )}
            </>
          )}

          {/* --- Render NESTED ARRAY Fields --- */}
          {field.type === "nestedArray" && (
            <div className="mt-4 border border-gray-200 rounded-md p-4 space-y-4 bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label || capitalizeFirstLetter(field.name)}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>

              {formData[field.name]?.map((nestedItem, index) => (
                <div
                  key={index}
                  className="border border-gray-300 rounded p-3 space-y-3 relative bg-white shadow-sm"
                >
                  {/* Only show remove button if there's more than one item */}
                  {formData[field.name].length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveNestedItem(field.name, index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-100"
                      aria-label="Remove item"
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  )}
                  <p className="text-xs font-semibold text-gray-500">
                    Item {index + 1}
                  </p>

                  {field.nestedFields.map((nestedField) => (
                    <div key={nestedField.name}>
                      <label
                        htmlFor={`${field.name}-${index}-${nestedField.name}`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        {nestedField.label ||
                          capitalizeFirstLetter(nestedField.name)}
                        {nestedField.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      {nestedField.type === "textarea" ? (
                        <textarea
                          id={`${field.name}-${index}-${nestedField.name}`}
                          name={nestedField.name}
                          value={nestedItem[nestedField.name] || ""}
                          onChange={(e) =>
                            handleInputChange(
                              e,
                              index,
                              nestedField.name,
                              field.name
                            )
                          }
                          rows={3}
                          required={nestedField.required}
                          placeholder={nestedField.placeholder || ""}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                      ) : nestedField.type === "select" ? ( // <<< Updated nested select rendering
                        <select
                          id={`${field.name}-${index}-${nestedField.name}`}
                          name={nestedField.name}
                          value={nestedItem[nestedField.name] || ""}
                          onChange={(e) =>
                            handleInputChange(
                              e,
                              index,
                              nestedField.name,
                              field.name
                            )
                          }
                          required={nestedField.required}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm"
                        >
                          <option value="" disabled={nestedField.required}>
                            {`Select ${
                              nestedField.label ||
                              capitalizeFirstLetter(nestedField.name)
                            }...`}
                          </option>
                          {(nestedField.options || []).map(
                            (option, optIndex) => {
                              // Determine value and label based on option structure (string or object)
                              const value =
                                typeof option === "string"
                                  ? option
                                  : option.value;
                              const label =
                                typeof option === "string"
                                  ? option
                                  : option.label;
                              return (
                                <option key={value || optIndex} value={value}>
                                  {" "}
                                  {/* Use index as fallback key */}
                                  {label}
                                </option>
                              );
                            }
                          )}
                        </select>
                      ) : (
                        // Default to text input for nested fields
                        <input
                          type={nestedField.type || "text"}
                          id={`${field.name}-${index}-${nestedField.name}`}
                          name={nestedField.name}
                          value={nestedItem[nestedField.name] || ""}
                          onChange={(e) =>
                            handleInputChange(
                              e,
                              index,
                              nestedField.name,
                              field.name
                            )
                          }
                          required={nestedField.required}
                          placeholder={nestedField.placeholder || ""}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}

              <button
                type="button"
                onClick={() => handleAddNestedItem(field.name)}
                className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-md hover:bg-blue-600 transition duration-150 ease-in-out shadow-sm"
              >
                <FaPlus size={12} />
                Add{" "}
                {capitalizeFirstLetter(
                  // Try to find a label, fallback to generic 'Item'
                  field.nestedFields
                    ?.find((nf) => nf.label)
                    ?.label?.split(" ")[0] || "Item"
                )}
              </button>
            </div>
          )}
        </div>
      ))}{" "}
      {/* End mapping top-level fields */}
      {/* --- Form Actions (Cancel/Save) --- */}
      <div className="flex justify-end gap-3 pt-6 border-t mt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className={`px-4 py-2 rounded-md text-white flex items-center justify-center gap-2 min-w-[100px] ${
            isSaving
              ? "bg-indigo-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700"
          }`}
        >
          {isSaving ? <FaSpinner className="animate-spin h-4 w-4" /> : null}
          {isSaving ? "Saving..." : "Save Item"}
        </button>
      </div>
    </form>
  );
};

export default AddItemForm;
